import React, { useState, useEffect } from 'react';
import { FaChevronLeft, FaChevronRight, FaTimes } from 'react-icons/fa';
import { getTransformedArtworksForOriginal } from '../utils/activityService';
import { auth } from '../firebase';
import { resolveImageUrl } from '../utils/urlResolver';

const TransformationNavigationModal = ({ 
  isOpen, 
  onClose, 
  originalArtwork, 
  originalArtworkTitle,
  initialActivity = null,
  onTutorialClick = null
}) => {
  const [transformedActivities, setTransformedActivities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [user, setUser] = useState(null);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      setUser(user);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (isOpen && originalArtwork && user) {
      fetchTransformedActivities();
    }
  }, [isOpen, originalArtwork, user]);

  useEffect(() => {
    if (initialActivity && transformedActivities.length > 0) {
      // Find the index of the initial activity
      const index = transformedActivities.findIndex(activity => activity.id === initialActivity.id);
      if (index !== -1) {
        setCurrentIndex(index);
      }
    }
  }, [initialActivity, transformedActivities]);

  const fetchTransformedActivities = async () => {
    if (!originalArtwork || !user) return;
    
    setLoading(true);
    try {
      const activities = await getTransformedArtworksForOriginal(originalArtwork, user.uid);
      // Filter to only include reimagine activities (not tutorials)
      const reimagineActivities = activities.filter(activity => activity.type === 'reimagine');
      setTransformedActivities(reimagineActivities);
      setCurrentIndex(0);
    } catch (error) {
      console.error('Error fetching transformed activities:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePrevious = () => {
    setCurrentIndex(prev => prev > 0 ? prev - 1 : transformedActivities.length - 1);
  };

  const handleNext = () => {
    setCurrentIndex(prev => prev < transformedActivities.length - 1 ? prev + 1 : 0);
  };

  const getExpandedImage = (activity) => {
    if (!activity) return '';
    
    // Check for journey steps first
    if (activity.journey && activity.journey.length > 0) {
      const lastStep = activity.journey[activity.journey.length - 1];
      if (lastStep.transformedImageUrl) {
        return getFullImageUrl(lastStep.transformedImageUrl);
      }
    }
    
    // Fallback to main transformed image
    if (activity.transformedImageUrl) {
      return getFullImageUrl(activity.transformedImageUrl);
    }
    
    // If no image found, return empty string
    return '';
  };

  const getFullImageUrl = (url) => {
    if (!url) return '';
    // Use centralized URL resolver to handle localhost:3001 URLs
    return resolveImageUrl(url);
  };

  const currentActivity = transformedActivities[currentIndex];

  // Expanded fullscreen modal
  if (expanded && currentActivity) {
    return (
      <div className="fixed inset-0 z-[9999] bg-black bg-opacity-90 flex items-center justify-center">
        <button
          onClick={() => setExpanded(false)}
          className="absolute top-6 right-6 bg-white/90 rounded-full p-3 shadow hover:bg-white z-60"
          title="Close"
        >
          <FaTimes size={28} />
        </button>
        <div className="flex flex-col items-center justify-center w-full h-full">
          <img
            src={getExpandedImage(currentActivity)}
            alt={`${currentActivity.styleInfo?.name || 'Transformed'} artwork`}
            className="w-full h-full max-w-4xl max-h-[90vh] object-contain rounded-2xl border-4 border-amber-400 shadow-2xl"
            style={{ background: 'white' }}
          />
        </div>
      </div>
    );
  }

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-7xl w-full max-h-[95vh] overflow-hidden">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b border-gray-200">
          <h2 className="text-2xl font-bold text-amber-800">
            Art Transformations - {originalArtworkTitle || 'Your Artwork'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 text-2xl font-bold"
          >
            ✕
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-500"></div>
              <span className="ml-3 text-amber-700 text-lg">Loading transformations...</span>
            </div>
          ) : transformedActivities.length === 0 ? (
            <div className="text-center py-12">
              <div className="text-6xl mb-4">🎨</div>
              <h3 className="text-2xl font-semibold text-amber-800 mb-2">No transformations yet</h3>
              <p className="text-amber-700">Transform this artwork to see it here!</p>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Navigation Info */}
              <div className="flex justify-between items-center bg-amber-50 rounded-lg p-4 border-2 border-amber-200">
                <div className="text-amber-800">
                  <span className="font-semibold">Transformation {currentIndex + 1} of {transformedActivities.length}</span>
                  {currentActivity && (
                    <span className="ml-2 text-amber-600">
                      - {currentActivity.styleInfo?.name || 'Art Style'}
                    </span>
                  )}
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={handlePrevious}
                    className="bg-amber-500 hover:bg-amber-600 text-white p-2 rounded-full transition-colors"
                    disabled={transformedActivities.length <= 1}
                  >
                    <FaChevronLeft size={16} />
                  </button>
                  <button
                    onClick={handleNext}
                    className="bg-amber-500 hover:bg-amber-600 text-white p-2 rounded-full transition-colors"
                    disabled={transformedActivities.length <= 1}
                  >
                    <FaChevronRight size={16} />
                  </button>
                </div>
              </div>

              {/* Results Grid */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                {/* Original */}
                <div className="original-result">
                  <h4 className="font-semibold text-amber-800 mb-3 text-center">Original Artwork</h4>
                  <div className="relative w-full h-[400px] rounded-2xl border-4 border-amber-200 shadow-lg overflow-hidden bg-white/90 backdrop-blur-sm">
                    <img 
                      src={getFullImageUrl(originalArtwork)} 
                      alt="Original artwork" 
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute bottom-2 left-2 text-center text-amber-700 font-semibold bg-white/80 px-2 py-1 rounded">
                      Original
                    </div>
                  </div>
                </div>
                
                {/* Transformed */}
                <div className="transformed-result">
                  <h4 className="font-semibold text-amber-800 mb-3 text-center">
                    {currentActivity?.styleInfo?.name || 'Transformed'} Style
                  </h4>
                  <div className="relative w-full h-[400px] rounded-2xl border-4 border-amber-200 shadow-lg overflow-hidden bg-white/90 backdrop-blur-sm cursor-pointer hover:scale-105 transition-transform duration-300 group"
                    onClick={() => setExpanded(true)}
                    onDoubleClick={() => setExpanded(true)}
                  >
                    <img 
                      src={getExpandedImage(currentActivity)} 
                      alt={`Artwork in ${currentActivity?.styleInfo?.name || 'Transformed'} style`} 
                      className="w-full h-full object-cover"
                      onLoad={() => {
                        console.log('✅ Navigation Modal Image loaded successfully:', getExpandedImage(currentActivity));
                      }}
                      onError={(e) => {
                        console.error('❌ Navigation Modal Failed to load image:', getExpandedImage(currentActivity));
                        console.error('❌ Navigation Modal Current activity:', currentActivity);
                        e.target.style.display = 'none';
                        // Show fallback content
                        const fallback = e.target.parentElement.querySelector('.image-fallback');
                        if (fallback) fallback.style.display = 'flex';
                      }}
                    />
                    {/* Fallback when image fails to load */}
                    <div className="image-fallback hidden absolute inset-0 items-center justify-center bg-amber-50">
                      <div className="text-center">
                        <div className="text-6xl mb-4">🎨</div>
                        <p className="text-lg font-semibold text-amber-700">
                          {currentActivity?.styleInfo?.name || 'Transformed'} Artwork
                        </p>
                        <p className="text-sm text-amber-600 mt-2">
                          Image loading...
                        </p>
                      </div>
                    </div>
                    <div className="absolute bottom-2 left-2 text-center text-amber-700 font-semibold bg-white/80 px-2 py-1 rounded">
                      {currentActivity?.styleInfo?.name || 'Transformed'}
                    </div>
                    <div className="absolute top-2 right-2 bg-amber-500 text-white px-3 py-1 rounded-full text-sm font-semibold opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      Double-click to expand!
                    </div>
                    <div className="absolute inset-0 bg-amber-500/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-2xl"></div>
                  </div>
                  <p className="text-sm text-amber-600 mt-2 text-center">
                    Double-click to learn more about {currentActivity?.styleInfo?.name || 'this style'}!
                  </p>
                  
                  {/* Tutorial Button */}
                  <div className="mt-4 text-center">
                    <button 
                      onClick={() => {
                        if (onTutorialClick && currentActivity) {
                          onTutorialClick(currentActivity);
                        } else {
                          console.log('Tutorial button clicked for:', currentActivity);
                        }
                      }}
                      className="bg-gradient-to-r from-orange-500 to-amber-400 hover:from-orange-600 hover:to-amber-500 text-white px-8 py-4 rounded-full text-xl font-bold transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105"
                      style={{ fontFamily: 'Baloo 2, Comic Sans MS, cursive' }}
                    >
                      View {currentActivity?.styleInfo?.name || 'Style'} Tutorial
                    </button>
                  </div>
                </div>
              </div>

              {/* Style Information */}
              {currentActivity?.styleInfo && (
                <div className="bg-amber-50 rounded-lg p-4 border-2 border-amber-200">
                  <h4 className="font-semibold text-amber-800 mb-2">About {currentActivity.styleInfo.name}</h4>
                  <p className="text-amber-700 text-sm">
                    {currentActivity.styleInfo.description || 'A beautiful art style transformation.'}
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TransformationNavigationModal;
